import React, { useEffect, useState } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { Box, Button, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import DefaultHeading from '../../../components/heading/DefaultHeading';
import './incentiveSummary.css'
import { ChannelkartNetworkGet, ChannelkartNetworkPut } from '../../../services/networkService';
import axios from 'axios';
import { getAccessToken } from '../../../services/authenticationService';
import { Center, LoadingOverlay } from '@mantine/core';
import { ConfirmationPopUp } from '../../../components/popup/confirmationPopUp';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faDeleteLeft, faEdit, faTrashAlt, faTrashCan, faTrashCanArrowUp, faX } from '@fortawesome/free-solid-svg-icons';
import RenderSvg from '../../../components/renderSvg/RenderSvg';
import { getEnvironmentData } from 'worker_threads';
import { EnvironmentRegistry } from '../../../services/registry/environmentRegistry';
import { updateIncentiveSummaryData } from '../../../stateManagement/actions/incentiveActions';

interface SummaryModel {
  createdOn: "05-02-2023",
  lastUpdatedOn: "07-02-2023",
  incentiveId: "",
  incentiveName: "Incentive 1",
  incentiveCalculation: "",
  incentiveFrequency: "",
  userSelection: "",    ////seprated by commas
  kpiChosen: string[],
  pointsDefined: number[],
  redemptionThreshold: number[],
  redemptionValue: number[],
  fromDate: "",
  toDate: "",
  isDeleted?: boolean;
}

interface FormattedModel {
  dateKey: String,
}
interface userKpi {
  userDesignation: String[],
  kpiSelected: String[]
}


const IncentiveSummary = () => {
  const [data, setData] = useState<SummaryModel[]>([]);
  const [filteredData, setFilteredData] = useState<SummaryModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortOption, setSortOption] = useState('');
  const [searchText, setSearchText] = useState<any>('');
  const [openDialog, setOpenDialog] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<string | null>(null);
  const [showEmptySvg, setShowEmptySvg] = useState(false)

  const valueMappingFromApi: any = {
    "MONTH": "MONTHLY",
    "PERIOD": "PERIOD",
    "WEEK": "WEEKLY",
    "SALESTARGET": "Sales Target",
    "CROSSSELL": "Cross Sell",
    "UPSELL": "Recover",
    "FOCUS": "Focus Product",
    "CROSSSELLPARENT": "Cross Sell",
    "UPSELLPARENT": "Recover",
    "FOCUSPARENT": "Focus Product"
  };


  const navigate = useNavigate();

  useEffect(() => {
    fetchIncentiveData();
  }, []);


  const fetchIncentiveData = async () => {
    setLoading(true);
    let response: any = {}
    // if (EnvironmentRegistry.getEnvironment() == 'uat') {
    //   response = await ChannelkartNetworkGet('https://reward-service-uat.salescode.ai/v1/incentiveinfo');
    // }

    // if (EnvironmentRegistry.getEnvironment() == 'demo') {
    //   response = await ChannelkartNetworkGet('https://rewardsdemo.sellina.io/v1/incentiveinfo');
    // }
    let rewardsUrl = EnvironmentRegistry.getRewardsUrl();
    response = await ChannelkartNetworkGet(`${rewardsUrl}/v1/incentiveinfo`);

    if (response && response?.data?.features) {
      const formattedData = response.data.features.map((feature: any) => ({
        createdOn: new Date(feature.creationTime),  // Extract only the date
        lastUpdatedOn: new Date(feature.lastModifiedTime),   // Extract only the date
        incentiveId: feature.id,
        incentiveName: feature.incentiveName,
        incentiveCalculation: feature.action,
        incentiveFrequency: valueMappingFromApi[feature.duration] || feature.duration,
        userSelection: feature?.designations?.join(", ") || "",
        kpiChosen: feature.kpiWithPoints.map((kpi: any) => valueMappingFromApi[kpi.name] || kpi.name),
        pointsDefined: feature.kpiWithPoints.map((kpi: any) => kpi.point),
        redemptionThreshold: feature.redemptionValue.map((r: any) => `${r.startPercentage}% - ${r.endPercentage}%`),
        redemptionValue: feature.redemptionValue.map((r: any) => `${r.value}`),
        fromDate: new Date(feature.startTime),  // Extract only the date
        toDate: new Date(feature.endTime),
        isDeleted: feature.activeStatus == "inactive" ? true : false // Extract only the date
      }));
      setData(formattedData);
      setFilteredData(formattedData);
      setLoading(false);

      let dataList: Map<string, string[]>[] = []; // Updated Map<string, string[]> to hold kpiChosen

      for (let formattedValue of formattedData) {
        let { fromDate, toDate, userSelection, kpiChosen, incentiveFrequency, isDeleted, incentiveCalculation , incentiveId } = formattedValue;
        if (isDeleted) {
          continue;
        }
        let users = userSelection.split(",").map((u: string) => u.trim()); // Split userSelection
        // For each user, create a key with 'fromDate~toDate~user'
        for (let user of users) {
          let dateKey = `${fromDate.toLocaleDateString()}~${toDate.toLocaleDateString()}~${user}~${incentiveFrequency}~${incentiveCalculation}~${incentiveId}`;

          ///remove the enteries which has end date less then 
          let toDateParts = toDate.toLocaleDateString().split("/"); // Assuming the format is dd/mm/yyyy
          let parsedToDate = new Date(
            parseInt(toDateParts[2]),  // year
            parseInt(toDateParts[1]) - 1,  // month (0-based index)
            parseInt(toDateParts[0])  // day
          );

          let today = new Date();
          // Skip entries whose toDate has already passed
          if (parsedToDate < today) {
            continue;
          }
          // Find if this key already exists
          let existingMap = dataList.find(map => map.has(dateKey));

          if (existingMap) {
            // If the key exists, update the kpiChosen array for this user
            let existingKpis = existingMap.get(dateKey)!;

            // Add unique KPIs to the existing KPI list
            existingKpis = Array.from(new Set([...existingKpis, ...kpiChosen]));

            // Update the map with the new KPIs
            existingMap.set(dateKey, existingKpis);
          } else {
            // Create a new map entry if this user for the date range doesn't exist
            let newEntry = new Map<string, string[]>();
            newEntry.set(dateKey, kpiChosen); // Set the initial kpiChosen for this user
            dataList.push(newEntry);
          }
        }
      }
      // Dispatch action to update the Redux store with the processed data
      updateIncentiveSummaryData(dataList);
    }
    else {
      setData([]);
      setFilteredData([]);
      setLoading(false);
      setShowEmptySvg(true);
    }
  }

  const columns = [
    {
      field: 'action',
      headerName: 'Action',
      width: 100,
      headerClassName: 'action-column-header',
      renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <div style={{ marginRight: '3px', marginTop: "3px" }}><RenderSvg name="incentive_action" /></div>
          <div style={{ marginLeft: '6px', fontWeight: 500 }}>Action</div>
        </div>
      ),
      renderCell: (params: any) => (
        <div style={{ display: "flex" }}>
          <div
            style={{
              display: "inline-block",
              cursor: new Date() >= new Date(params.row.fromDate) ? "not-allowed" : "pointer",
            }}
          >
            <Button
              style={{ minWidth: "30px" }}
              onClick={() => handleEdit(params.row)}
              disabled={new Date() >= new Date(params.row.fromDate)} // Disable if today's date >= fromDate
            >
              <RenderSvg name="edit_incentive" />
            </Button>
          </div>         <div >
            <Button style={{
              minWidth: "30px",
              cursor: params.row.isDeleted ? 'not-allowed' : 'pointer', // Disable pointer when disabled
              opacity: params.row.isDeleted ? 0.5 : 1, // Reduce opacity to indicate disabled state
            }} onClick={() => {
              if (!params.row.isDeleted) {
                setRowToDelete(params.row.incentiveId); setOpenDialog(true); // Only call handleEdit if not disabled
              }
            }}><RenderSvg name="delete_incentive" /></Button>
          </div>

        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'createdOn', width: 140, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="created_on" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Created{"\n"}On</div>
        </div>
      ),
      disableColumnMenu: true,
      valueGetter: (params: any) => params.value && new Date(params.value),
      valueFormatter: (params: any) => params.value ? params.value.toLocaleDateString() : '',  // Format the date for display
    },
    {
      field: 'lastUpdatedOn', headerName: 'Last Updated On', width: 160, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="last_updated" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Last{"\n"} Updated On</div>
        </div>
      ),
      disableColumnMenu: true,
      valueGetter: (params: any) => params.value && new Date(params.value),
      valueFormatter: (params: any) => params.value ? params.value.toLocaleDateString() : '',  // Format the date for display
    },
    {
      field: 'incentiveId', headerName: 'Incentive ID', width: 150, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="incentive_id" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Incentive{"\n"} ID</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'incentiveName', headerName: 'Incentive Name', width: 160, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="incentive_id" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Incentive{"\n"} Name</div>
        </div>
      ),
      // sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'incentiveCalculation', headerName: 'Incentive Calculation', width: 150, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="calculator" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Incentive{"\n"} Calculation</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'incentiveFrequency', headerName: 'Incentive Frequency', width: 150, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="incentive_frequency" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Incentive{"\n"} Frequency</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'userSelection', headerName: 'User Selection', width: 180, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="role_selection" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Role{"\n"} Selection</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },

    // Displaying arrays on new lines
    {
      field: 'kpiChosen',
      headerName: 'KPI Chosen',
      width: 150, headerClassName: 'custom-header-createdOn',
      renderCell: (params: any) => (
        <div style={{
          whiteSpace: 'pre-line',    // Keep multiline formatting
          maxHeight: '60px',         // Set max height for the cell content
          overflowY: 'auto',         // Enable vertical scrolling
          padding: '5px',
        }}>
          {params.row.kpiChosen.join('\n')}
        </div>
      ), renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="kpi_chosen" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>KPI{"\n"} Chosen</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'pointsDefined',
      headerName: 'Points Defined',
      width: 140,
      renderCell: (params: any) => (
        <div style={{
          whiteSpace: 'pre-line',    // Keep multiline formatting
          maxHeight: '60px',         // Set max height for the cell content
          overflowY: 'auto',         // Enable vertical scrolling
          padding: '5px',
        }}>
          {params.row.pointsDefined.join('\n')}
        </div>
      ), renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="points_defined" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Points{"\n"} Defined</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'redemptionThreshold',
      headerName: 'Redemption Threshold',
      width: 180,
      renderCell: (params: any) => (
        <div style={{
          whiteSpace: 'pre-line',    // Keep multiline formatting
          maxHeight: '60px',         // Set max height for the cell content
          overflowY: 'auto',         // Enable vertical scrolling
          padding: '5px',
        }}>
          {params.row.redemptionThreshold.join('\n')}
        </div>
      ), renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="redemption_threshold" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Redemption{"\n"} Threshold</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'redemptionValue',
      headerName: 'Redemption Value',
      width: 170,
      renderCell: (params: any) => (
        <div style={{
          whiteSpace: 'pre-line',    // Keep multiline formatting
          maxHeight: '60px',         // Set max height for the cell content
          overflowY: 'auto',         // Enable vertical scrolling
          padding: '5px',
        }}>
          {params.row.redemptionValue.join('\n')}
        </div>
      ), renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="redemption_value" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>Redemption{"\n"} value</div>
        </div>
      ),
      sortable: false,
      disableColumnMenu: true,
    },
    {
      field: 'fromDate', headerName: 'From Date', width: 140, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="calendar" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>From{"\n"} Date</div>
        </div>
      ),
      disableColumnMenu: true,
      valueGetter: (params: any) => params.value && new Date(params.value),
      valueFormatter: (params: any) => params.value ? params.value.toLocaleDateString() : '',  // Format the date for display
    },
    {
      field: 'toDate', headerName: 'To Date', width: 140, renderHeader: () => (
        <div style={{ display: 'flex', alignItems: 'center', whiteSpace: 'pre-line', lineHeight: "18px" }}>
          <RenderSvg name="calendar" />
          <div style={{ marginLeft: '12px', fontWeight: 500 }}>To{"\n"} Date</div>
        </div>
      ),
      disableColumnMenu: true,
      valueGetter: (params: any) => params.value && new Date(params.value),
      valueFormatter: (params: any) => params.value ? params.value.toLocaleDateString() : '',  // Format the date for display
    }
  ];


  const handleEdit = (row: any) => {
    console.log("Editing row:", row);
    const dataToEdit: any = {
      "calculationType": {
        "value": row.incentiveCalculation,
      },
      "period": {
        "value": valueMappingFromApi[row.incentiveFrequency] || row.incentiveFrequency
      },
      "incentiveName": {
        "value": row.incentiveName
      },
      "storedDates": {
        "value":
        {
          "startDate": row.fromDate.toLocaleDateString(),
          "endDate": row.toDate.toLocaleDateString()
        }

      },
    };
    const userSelectionArray = row.userSelection.split(", ")
    const userObject = []
    for (let user of userSelectionArray) {
      userObject.push({
        "name": user,
        "points": 0
      })
    }
    dataToEdit["user"] = {
      "value": userObject
    }
    let kpiArray = row.kpiChosen.map((kpi: any, index: any) => {
      return {
        name: valueMappingFromApi[kpi] || kpi,
        points: row.pointsDefined[index]
      };
    });
    dataToEdit["kpi"] = {
      "value": kpiArray
    }
    let rangeArray = row.redemptionThreshold.map((threshold: any, index: any) => {
      let [fromRange, toRange] = threshold.replace(/%/g, '').split(' - ').map(Number);
      let rangeValue = parseInt(row.redemptionValue[index].replace('₹', ''));

      return {
        fromRange: fromRange,
        toRange: toRange,
        rangeValue: rangeValue
      };
    });
    dataToEdit["threshold"] = {
      "value": rangeArray
    }
    Object.keys(dataToEdit).forEach((key) => {
      dataToEdit[key].isOpen = true;
      dataToEdit[key].isValid = true;
    });
    localStorage.incentiveData = JSON.stringify(dataToEdit);
    localStorage.editIncentiveID = row.incentiveId
    navigate('create');
  };

  const handleDelete = async (row: any) => {
    if (rowToDelete) {
      try {
        let response: any = {};
        let requestBody = {
          "id": rowToDelete,
          "activeStatus": "inactive"
        }
        // if (EnvironmentRegistry.getEnvironment() == 'uat') {
        //   response = await ChannelkartNetworkPut('https://reward-service-uat.salescode.ai/v1/incentiveinfo', requestBody);
        // }

        // if (EnvironmentRegistry.getEnvironment() == 'demo') {
        //   response = await ChannelkartNetworkPut('https://rewardsdemo.sellina.io/v1/incentiveinfo', requestBody);
        // }

        let rewardsUrl = EnvironmentRegistry.getRewardsUrl();
        response = await ChannelkartNetworkPut(`${rewardsUrl}/v1/incentiveinfo` ,requestBody);
        if (response.status === 200) {
          console.log('Incentive data posted successfully:', response.data);
        } else {
          console.error('Failed to post incentive data:', response.status);
        }

        // Mark the item as deleted in the state
        const updatedData = filteredData.map(row =>
          row.incentiveId === rowToDelete ? { ...row, isDeleted: true } : row
        );
        setData(updatedData);
        setFilteredData(updatedData);
        setOpenDialog(false);
        setRowToDelete(null);
        fetchIncentiveData();
        console.log("Deleted row with ID:", rowToDelete);
      } catch (error) {
        console.error("Error deleting item:", error);
      }
    }
  };

  const search = (e: any) => {
    const value = e.target.value.toLowerCase();
    setSearchText(value);

    if (value === '') {
      // If search text is cleared, reset filteredData to original data
      setFilteredData(data);
    } else {
      const filteredRows = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];

        let matches = false;

        // Check if any KPI in kpiChosen matches the search term
        for (let j = 0; j < row.kpiChosen.length; j++) {
          if (row.kpiChosen[j].toLowerCase().includes(value)) {
            matches = true;
            break;
          }
        }

        // Check if redemptionThreshold matches the search term
        for (let k = 0; k < row.redemptionThreshold.length; k++) {
          if (row.redemptionThreshold[k] == value) {
            matches = true;
            break;
          }
        }

        // Check if userSelection, incentiveName, or incentiveId matches the search term
        if (
          row.userSelection?.toLowerCase().includes(value) ||
          row.incentiveName?.toLowerCase().includes(value) ||
          row.incentiveId?.toLowerCase().includes(value)
        ) {
          matches = true;
        }

        // If any of the above conditions matched, add the row to filteredRows
        if (matches) {
          filteredRows.push(row);
        }
      }

      setFilteredData(filteredRows);
    }
  };

  return !loading ? (
    <div className='incentive-summary-body'>
      <DefaultHeading heading='Incentive Summary' showDefaultBackButton={true} />
      <div className="incentiveDataGridContainer">
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', width: "30%" }} >
            <div style={{ width: 300 }}>
              <TextField
                value={searchText}
                onChange={search}
                placeholder='Search'
                sx={{
                  height: 35, marginTop: 2, marginBottom: 1,
                  '& .MuiInputBase-root': { height: 35 },
                  '& input': { fontSize: '0.8rem' },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderWidth: '1px', // Decreases the border thickness
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </div>
          </div>
          <Button
            variant="contained"
            sx={{
              marginLeft: 2,
              height: 35,
              fontSize: '0.8rem',
              marginTop: 2,
              marginRight: 1
            }}
            startIcon={<AddIcon />}
            onClick={() => {
              delete localStorage.incentiveData;
              delete localStorage.editIncentiveID;
              navigate('create');
            }}
          >
            Create Incentive
          </Button>
        </div>
        <div style={{ marginTop: "20px" }}>
          {!showEmptySvg ?
            <DataGrid className='live_dashboard_table'
              getRowId={(data: any) => data.incentiveId}
              rows={filteredData}
              rowHeight={80}
              columns={columns}
              autoHeight
              getRowClassName={(params) =>
                params.row.isDeleted ? 'row-deleted' : ''  // Apply gray-out style
              }
              sx={{
                paddingInline: '0px',  // Remove padding
                margin: '0px',         // Remove margin

                // Center the headers and their SVGs
                '& .MuiDataGrid-columnHeaderTitleContainer': {
                  justifyContent: 'center',
                  alignItems: 'center',
                },

                // Center the content inside the cells
                '& .MuiDataGrid-cell': {
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  fontWeight: 500,
                  color: '#909090',
                  // borderRight: '1px solid #E9EFF5',
                  borderBottom: '1px solid #E9EFF5' // Thinner grid lines
                },

                // Center the headers and apply thinner grid lines
                '& .MuiDataGrid-columnHeaders': {
                  borderRight: '0.1px solid #E9EFF5', // Thinner grid lines
                },

                // Decrease the width of the grid lines
                '& .MuiDataGrid-columnHeader': {
                  borderRight: '0.1px solid #E9EFF5', // Thinner grid lines
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold'
                }
              }}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 25,
                  },
                },
              }}
              pageSizeOptions={[25]}
              disableRowSelectionOnClick
            />
            : <Center><RenderSvg name="empty_incentive_svg" /></Center>
          }

        </div>

        <ConfirmationPopUp
          setOpenConfirmModal={setOpenDialog}
          message="Are you sure you want to deactivate this incentive?"
          openConfirmModal={openDialog}
          successMethod={async () => {
            setRowToDelete(rowToDelete)
            handleDelete(rowToDelete)
          }}
        />
      </div>
    </div>
  ) : <LoadingOverlay
    loaderProps={{ size: 'sm', color: 'teal', variant: 'bars' }}
    visible={loading}
    style={{ position: "fixed" }}
  />
};

export default IncentiveSummary;
